import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  createAdminUser,
  formatAdminApiError,
  listAdminUsers,
  updateAdminUser,
} from "@/lib/adminUsersApi";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";

function roleLabel(role) {
  if (role === "admin") return "Admin";
  if (role === "user") return "User";
  return "Unassigned";
}

function roleBadgeVariant(role) {
  if (role === "admin") return "default";
  if (role === "user") return "secondary";
  return "outline";
}

/**
 * Admin-only user & role management (HALO-73 / HALO-74).
 */
export default function SettingsUserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingUsername, setSavingUsername] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAdminUsers();
      setUsers(data.users || []);
    } catch (err) {
      const message = formatAdminApiError(err);
      setError(message);
      setUsers([]);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleRoleChange = async (username, role) => {
    setSavingUsername(username);
    try {
      const updated = await updateAdminUser(username, { role });
      setUsers((prev) =>
        prev.map((u) => (u.username === username ? updated : u)),
      );
      toast.success(`Role updated to ${roleLabel(role)}`);
    } catch (err) {
      toast.error(formatAdminApiError(err));
    } finally {
      setSavingUsername(null);
    }
  };

  const handleToggleEnabled = async (row) => {
    const nextEnabled = !row.enabled;
    if (!nextEnabled && row.username === currentUser?.sub) {
      toast.error("You cannot disable your own account.");
      return;
    }
    setSavingUsername(row.username);
    try {
      const updated = await updateAdminUser(row.username, { enabled: nextEnabled });
      setUsers((prev) =>
        prev.map((u) => (u.username === row.username ? updated : u)),
      );
      toast.success(nextEnabled ? "User enabled" : "User disabled");
    } catch (err) {
      toast.error(formatAdminApiError(err));
    } finally {
      setSavingUsername(null);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) {
      toast.error("Email is required");
      return;
    }
    setInviteSubmitting(true);
    try {
      const created = await createAdminUser({ email, role: inviteRole });
      setUsers((prev) => {
        const exists = prev.some((u) => u.username === created.username);
        return exists
          ? prev.map((u) => (u.username === created.username ? created : u))
          : [created, ...prev];
      });
      toast.success("Invitation sent. The user will receive a temporary password by email.");
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("user");
    } catch (err) {
      toast.error(formatAdminApiError(err));
    } finally {
      setInviteSubmitting(false);
    }
  };

  return (
    <Card data-testid="settings-user-management">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            User management
          </CardTitle>
          <CardDescription>
            Invite users, assign Admin or User role, and enable or disable accounts in
            Cognito.
          </CardDescription>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadUsers}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button type="button" size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Invite user
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleInvite}>
                <DialogHeader>
                  <DialogTitle>Invite user</DialogTitle>
                  <DialogDescription>
                    Cognito sends an email with a temporary password. The user must set a
                    new password on first sign-in.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      autoComplete="off"
                      value={inviteEmail}
                      onChange={(ev) => setInviteEmail(ev.target.value)}
                      placeholder="name@company.com"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="invite-role">Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger id="invite-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setInviteOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={inviteSubmitting}>
                    {inviteSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Send invite"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading users…
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((row) => {
                    const busy = savingUsername === row.username;
                    const isSelf = row.username === currentUser?.sub;
                    const roleValue = row.role || "none";
                    return (
                      <TableRow key={row.username}>
                        <TableCell>
                          <div className="font-medium">{row.email || "—"}</div>
                          <div className="text-xs text-muted-foreground font-mono truncate max-w-[220px]">
                            {row.username}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={roleBadgeVariant(row.role)}>
                            {roleLabel(row.role)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{row.status || "—"}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={row.enabled ? "secondary" : "destructive"}>
                            {row.enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Select
                              value={roleValue}
                              disabled={busy}
                              onValueChange={(value) => {
                                if (value === "none") return;
                                handleRoleChange(row.username, value);
                              }}
                            >
                              <SelectTrigger className="w-[120px]" aria-label="Change role">
                                <SelectValue placeholder="Role" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                {roleValue === "none" ? (
                                  <SelectItem value="none" disabled>
                                    Unassigned
                                  </SelectItem>
                                ) : null}
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={busy || isSelf}
                              onClick={() => handleToggleEnabled(row)}
                            >
                              {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : row.enabled ? (
                                "Disable"
                              ) : (
                                "Enable"
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
